// =================================================================
// useIndividualUser hook
//
// Manages the "individual subscriber" identity — a Firebase Auth user
// who has a corresponding /brain_health/users/{uid}/ record. This is
// the $4.99/month self-serve consumer path.
//
// Schema (snake_case, flat under $uid, mirroring /brain_health/accounts/):
//
//   /brain_health/users/{uid}/
//     display_name
//     email
//     created_at
//     last_active
//     acquisition_source         ('self_serve' | 'device_bundled' | 'community_referral')
//     family_contact?            (optional email for monthly summary)
//     assessment/                (mirrors /accounts/ assessment shape)
//     goals/
//     sessions/
//     subscription/              (server-only writes — Stripe webhook)
//       stripe_customer_id
//       stripe_subscription_id
//       status                   ('active' | 'trialing' | 'past_due' | 'canceled' | ...)
//       plan
//       current_period_end
//
// Entitlement state lives in /brain_health/entitlements/{uid}/ (the
// existing unified entitlement ledger — see database.rules.json).
// Phase 2A doesn't subscribe to that node yet; we derive entitlement
// from subscription.status directly. Phase 2B may switch to reading
// from entitlements/ for the unified view.
//
// Distinction worth holding clearly:
//   - useAuth: tracks Firebase Auth. Returns a user OR null. A user
//     here might be an admin (super or community), an individual
//     subscriber, or a freshly-authenticated email with no role yet.
//   - useIndividualUser: layered on top of useAuth. Returns a user
//     record IFF the auth user has a /brain_health/users/{uid}/ node
//     in the database. That's the marker of "this auth identity is
//     an individual brain health subscriber, not (only) an admin."
//
// An auth identity could in principle be both an admin AND an
// individual subscriber (e.g., the founder testing the consumer
// flow). That's fine — useAdminAccess and useIndividualUser are
// independent, useCurrentContext picks one based on priority.
// =================================================================

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from './firebase.js';
import { useAuth } from './useAuth.jsx';

const IndividualUserContext = createContext(null);

// Subscription statuses that grant access to the product.
//
// - 'active'   : paying, current. Obvious yes.
// - 'trialing' : Stripe trial period (we don't use trials today but
//   the status is included so future trial offers Just Work).
// - 'past_due' : a payment failed and Stripe is in retry mode.
//   Including this is a deliberate grace-period decision: Stripe's
//   Smart Retries run for ~3 weeks before giving up, and during
//   that window we keep access on so a temporary card hiccup (an
//   expired card, an out-of-town bank flag, a typo'd CVV) doesn't
//   lock someone out of the brain health tools they're using daily.
//   When retries finally exhaust, Stripe fires customer.subscription
//   .deleted and the webhook flips status to 'canceled' — at which
//   point this set no longer matches and access ends cleanly.
//   The Account page's "Manage subscription" link opens the Stripe
//   Customer Portal where the user can update their card to recover.
//
// Statuses NOT included (intentionally → access ends):
//   'incomplete', 'incomplete_expired' : initial payment failed
//                                        before the sub ever activated
//   'unpaid'                           : Stripe gave up on retries
//   'canceled'                         : explicit cancellation
const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'past_due']);

export function IndividualUserProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();

  const [userRecord, setUserRecord] = useState(null);

  // -----------------------------------------------------------------
  // The recordLoading state — and why it's two pieces, not one.
  //
  // The race we're closing here: the app can transition from "no auth
  // user" to "auth user present" mid-session (e.g., user clicks a
  // magic link, completes sign-in while app is already mounted). In
  // that transition there's a render where authUser is truthy but the
  // useEffect below — which actually starts the onValue subscription
  // and sets recordLoading=true — hasn't run yet (effects fire AFTER
  // render). In that gap, consumers see authLoading=false, authUser
  // present, recordLoading=false (from the prior "no auth" branch),
  // and userRecord=null. They reasonably conclude "this auth user has
  // no individual record" and act on that — Signup.jsx for example
  // routes the user to the new-user display-name flow when it really
  // should redirect a returning user to /account.
  //
  // The fix: track which authUser the last successful onValue tick
  // was for. If that tracker doesn't match the current authUser
  // (because authUser just changed and the effect hasn't caught up
  // yet), report loading=true. The effect below updates the tracker
  // when each onValue snapshot lands.
  //
  // This is more bulletproof than a useState initialized to true:
  // useState only protects the first mount. The tracker approach
  // protects every authUser transition, including mid-session ones.
  // -----------------------------------------------------------------
  const [recordLoading, setRecordLoading] = useState(true);
  // Holds the authUser uid that the current userRecord corresponds
  // to. null when no fetch has completed yet, or when the last
  // settled state was "no authUser." Compared against the live
  // authUser to detect "we have a new auth identity but haven't
  // fetched its record yet" — see derivation below.
  const lastSettledUidRef = useRef(null);

  useEffect(() => {
    if (authLoading) {
      // Wait for auth to settle before touching the DB.
      return;
    }
    if (!authUser) {
      setUserRecord(null);
      setRecordLoading(false);
      lastSettledUidRef.current = null;
      return;
    }
    // New auth user (or first time we're seeing this one). Reset
    // userRecord so consumers don't see stale data from a prior
    // auth identity, mark loading, and start the subscription.
    setUserRecord(null);
    setRecordLoading(true);
    const targetUid = authUser.uid;
    const userRef = ref(db, `brain_health/users/${targetUid}`);
    const unsubscribe = onValue(
      userRef,
      (snap) => {
        const val = snap.val();
        setUserRecord(val ? { id: targetUid, ...val } : null);
        setRecordLoading(false);
        lastSettledUidRef.current = targetUid;
      },
      (err) => {
        // Permission denied is the *expected* outcome for an admin
        // user with no /users/ record — rules will reject the read.
        // That just means they're not an individual subscriber, which
        // is fine. Only log unexpected errors.
        if (err.code !== 'PERMISSION_DENIED') {
          console.error('Individual user read failed:', err);
        }
        setUserRecord(null);
        setRecordLoading(false);
        lastSettledUidRef.current = targetUid;
      }
    );
    return unsubscribe;
  }, [authUser, authLoading]);

  // Synchronous loading derivation, evaluated every render.
  // recordLoading from useState is updated by the effect, but effects
  // run AFTER render. So during the render where authUser first
  // appears, the effect hasn't run yet to set recordLoading=true
  // (and reset userRecord). The ref comparison catches that gap:
  // if we have an authUser but our last settled fetch wasn't for
  // THIS authUser, we're loading regardless of the state value.
  const effectiveRecordLoading =
    recordLoading ||
    (!!authUser && lastSettledUidRef.current !== authUser.uid);

  // Derived entitlement state. A user is entitled if they have a
  // subscription whose status is in ACTIVE_SUBSCRIPTION_STATUSES
  // (active, trialing, or past_due — see the constant's comment for
  // the grace-period reasoning). Perpetual grants for individuals
  // (e.g., promotional lifetime access) would arrive via the Stripe
  // webhook or admin tooling writing the entitlements/ ledger; for
  // Phase 2A we only check subscription state here. Phase 2B may
  // extend this to read /brain_health/entitlements/{uid}/ directly.
  const hasActiveSubscription =
    !!userRecord?.subscription &&
    ACTIVE_SUBSCRIPTION_STATUSES.has(userRecord.subscription.status);
  const hasEntitlement = hasActiveSubscription;

  const value = {
    userRecord,
    loading: authLoading || effectiveRecordLoading,
    // Has a /users/{uid}/ record at all (regardless of payment state).
    // Useful for distinguishing "subscriber whose card just failed"
    // from "this is an admin, not a subscriber."
    isActive: !!userRecord,
    hasEntitlement,
    hasActiveSubscription,
  };

  return (
    <IndividualUserContext.Provider value={value}>
      {children}
    </IndividualUserContext.Provider>
  );
}

export function useIndividualUser() {
  const ctx = useContext(IndividualUserContext);
  if (!ctx) {
    throw new Error(
      'useIndividualUser must be used inside <IndividualUserProvider>'
    );
  }
  return ctx;
}
